import type { CSSProperties } from 'react';
import { Field } from '../../components/Field';
import { DatePicker } from '../../components/DatePicker';
import { TimeSelect } from '../../components/TimeSelect';
import { PS, T } from '../../lib/theme';
import {
  LOCALE_LABELS,
  TOURNAMENT_LOCALES,
  TTAR_RESULT_INPUT_MODE_LABELS,
} from '../../lib/tournaments';
import { WEEKDAY_LABELS } from '../../lib/coach-series';
import type { TournamentSeriesFormValue } from '../../lib/tournament-series-form';
import type { TableEmbed, TournamentLocale } from '../../lib/types';

const WEEKDAY_OPTIONS = WEEKDAY_LABELS.map((label, value) => ({ value, label }));

interface Props {
  value: TournamentSeriesFormValue;
  onChange: (patch: Partial<TournamentSeriesFormValue>) => void;
  tables: TableEmbed[];
  tablesLoading: boolean;
}

/**
 * The field set shared by "create series" and "edit series": three locale
 * sections (title + description), weekday + time window, table
 * multi-select, fee, capacity, series date range, image.
 *
 * No registration-deadline section here (unlike `TournamentFieldsForm`) —
 * `prospin-api`'s tournament-series contract has no such field; see
 * `lib/tournament-series-form.ts`'s module doc. No frozen-field disabling
 * either: a series-rule PATCH is never rejected the way a one-off's is —
 * the API's freeze lives at the per-occurrence level (see the schedule-field
 * note below), not on the rule row this form edits.
 */
export function TournamentSeriesFieldsForm({ value, onChange, tables, tablesLoading }: Props) {
  const setTranslation = (locale: TournamentLocale, field: 'title' | 'description', v: string) => {
    onChange({
      translations: {
        ...value.translations,
        [locale]: { ...value.translations[locale], [field]: v },
      },
    });
  };

  const toggleTable = (id: number) => {
    const next = value.tableIds.includes(id)
      ? value.tableIds.filter((t) => t !== id)
      : [...value.tableIds, id];
    onChange({ tableIds: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <section style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Localized content</h2>
        {TOURNAMENT_LOCALES.map((locale) => (
          <div
            key={locale}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              border: `1px solid ${PS.borderLight}`,
              borderRadius: 4,
              padding: 12,
            }}
          >
            <span style={{ ...T.label }}>{LOCALE_LABELS[locale]}</span>
            <Field label="Title">
              <input
                type="text"
                value={value.translations[locale].title}
                onChange={(e) => setTranslation(locale, 'title', e.target.value)}
                style={inputStyle}
                aria-label={`${LOCALE_LABELS[locale]} title`}
              />
            </Field>
            <Field label="Description (optional)">
              <textarea
                value={value.translations[locale].description}
                onChange={(e) => setTranslation(locale, 'description', e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' as const }}
                aria-label={`${LOCALE_LABELS[locale]} description`}
              />
            </Field>
          </div>
        ))}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Cadence, time &amp; tables</h2>
        <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
          Weekday, time window, table selection and the date range below only affect occurrences
          the materializer has not created yet. They never retroactively move an
          already-materialized occurrence — reschedule one individually from its own tournament
          page instead.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Weekday">
            <select
              value={value.dayOfWeek}
              onChange={(e) => onChange({ dayOfWeek: Number(e.target.value) })}
              style={selectStyle}
            >
              {WEEKDAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Start time">
            <TimeSelect
              value={value.startTime}
              onChange={(v) => onChange({ startTime: v })}
              min="00:00"
              max="24:00"
              ariaLabel="Start time"
            />
          </Field>
          <Field label="End time">
            <TimeSelect
              value={value.endTime}
              onChange={(v) => onChange({ endTime: v })}
              min="00:00"
              max="24:00"
              includeMax
              ariaLabel="End time"
            />
          </Field>
          <Field label="Warmup (min)">
            <input
              type="number"
              min={0}
              max={120}
              step={5}
              value={value.warmupMinutes}
              onChange={(e) => onChange({ warmupMinutes: e.target.value })}
              style={inputStyle}
              aria-label="Warmup (min)"
            />
          </Field>
        </div>

        <Field label="Tables">
          {tablesLoading ? (
            <span style={{ ...T.caption, color: PS.textSec }}>Loading tables…</span>
          ) : tables.length === 0 ? (
            <span style={{ ...T.caption, color: PS.textSec }}>No tables found.</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {tables.map((t) => (
                <label
                  key={t.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    border: `1px solid ${PS.border}`,
                    borderRadius: 3,
                    ...T.ui,
                    minHeight: 40,
                    boxSizing: 'border-box',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={value.tableIds.includes(t.id)}
                    onChange={() => toggleTable(t.id)}
                  />
                  {t.name}
                </label>
              ))}
            </div>
          )}
        </Field>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Series starts on">
            <DatePicker
              value={value.startsOn}
              onChange={(v) => onChange({ startsOn: v })}
              ariaLabel="Series starts on"
            />
          </Field>
          <Field label="Series ends on (optional)">
            <DatePicker
              value={value.endsOn}
              onChange={(v) => onChange({ endsOn: v })}
              ariaLabel="Series ends on"
            />
          </Field>
        </div>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Fee &amp; capacity</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="Fee (GEL)">
            <input
              type="number"
              min={0}
              step="0.01"
              value={value.feeGel}
              onChange={(e) => onChange({ feeGel: e.target.value })}
              style={inputStyle}
              aria-label="Fee in GEL"
            />
          </Field>
          <Field label="Capacity (optional)">
            <input
              type="number"
              min={1}
              step={1}
              value={value.maxParticipants}
              onChange={(e) => onChange({ maxParticipants: e.target.value })}
              style={inputStyle}
              aria-label="Capacity"
              placeholder="Unlimited"
            />
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Field label="TTAR rating from">
            <input
              type="number"
              min={1}
              step={1}
              value={value.minTtarRating}
              onChange={(e) => onChange({ minTtarRating: e.target.value })}
              style={inputStyle}
              aria-label="TTAR rating from"
              placeholder="Any"
            />
          </Field>
          <Field label="TTAR rating to">
            <input
              type="number"
              min={1}
              step={1}
              value={value.maxTtarRating}
              onChange={(e) => onChange({ maxTtarRating: e.target.value })}
              style={inputStyle}
              aria-label="TTAR rating to"
              placeholder="Any"
            />
          </Field>
        </div>
        <Field label="TTAR results">
          <select
            value={value.ttarResultInputMode}
            onChange={(e) => onChange({ ttarResultInputMode: e.target.value as typeof value.ttarResultInputMode })}
            style={selectStyle}
            aria-label="TTAR results"
          >
            {Object.entries(TTAR_RESULT_INPUT_MODE_LABELS).map(([mode, label]) => (
              <option key={mode} value={mode}>{label}</option>
            ))}
          </select>
          <span style={{ ...T.caption, color: PS.textSec }}>
            Applies when the tournament is created in TTAR.
          </span>
        </Field>
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ ...T.subheading, margin: 0 }}>Image</h2>
        <Field label="Image URL (optional)">
          <input
            type="text"
            value={value.imageUrl}
            onChange={(e) => onChange({ imageUrl: e.target.value })}
            style={inputStyle}
            placeholder="https://…"
            aria-label="Image URL"
          />
        </Field>
      </section>
    </div>
  );
}

const selectStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  background: PS.white,
};

const inputStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  background: PS.white,
  boxSizing: 'border-box',
  width: '100%',
};
